import { useDeferredValue, useMemo, useState } from 'react'
import type { PaymentsTaken, SaleView } from '../api/types'
import { DayAverageByItem } from '../components/DayAverageByItem'
import {
  Amt,
  Card,
  Dt,
  Empty,
  Field,
  LONG_LIST_CAP,
  LongListNote,
  Note,
  PageHead,
  TotalBox,
  Wt,
} from '../components/ui'
import { useApi } from '../hooks/useApi'
import { addDays, formatEnteredAt, today } from '../lib/format'

/**
 * One shared empty array for "nothing has arrived yet".
 *
 * Writing `sales.data ?? []` inline would build a new one on every render, and
 * useDeferredValue compares by identity - a value that is never equal to the
 * last one defers forever, and the page renders everything twice for good.
 */
const NOTHING_YET: SaleView[] = []

/**
 * A date this many days back, as the API wants it.
 *
 * <p>Built from `today()` rather than from `toISOString()` on a raw Date. The
 * two disagree for the whole first half of a Karachi morning: the shop is five
 * hours ahead of UTC and a flower market opens before dawn, so at 4am on the
 * 5th `toISOString()` still says the 4th. The buttons on this page would have
 * been quietly reporting a window one day out during exactly the hours the shop
 * does its selling.
 */
function daysBack(days: number): string {
  return addDays(today(), -days)
}

/**
 * Every sale over a span, one line each.
 *
 * <p>The day book answers what the drawer did; this answers what was sold. A
 * shopkeeper who writes sales all day wants to be able to look back over a week
 * and see the entries themselves - which flower, what weight, whose name if it
 * went on credit - not just a total he has to take on trust.
 *
 * <p>Cash and credit sit in one list because the question is what left the shop.
 * The two are still added up apart underneath, since only one of them is money
 * in hand.
 */
export function SalesReport() {
  /*
   * Opens on today.
   *
   * The question this page is opened with, nine times out of ten, is "what has
   * gone out today" - and that is asked in the middle of a working day, not at
   * the end of a month. The wider spans are one tap away underneath, which is
   * the right way round: a shop should not have to narrow a report down to see
   * the day it is standing in.
   */
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())

  const sales = useApi<SaleView[]>(`/sales?from=${from}&to=${to}`, [from, to])

  /*
   * What came back in over the same days.
   *
   * <p>Selling and being paid are two different days, and the pair is the
   * question actually being asked: a morning can put 77,000 over the counter
   * and take 14,000 in hand, with the rest walking out as اُدھار. One number
   * from the server rather than four hundred rows summed here.
   */
  const taken = useApi<PaymentsTaken>(`/reports/payments-taken?from=${from}&to=${to}`, [
    from,
    to,
  ])
  const rows = sales.data ?? NOTHING_YET

  /*
   * Lets the page answer a tap while the table is still being built.
   *
   * The two hundred rows below are drawn at low priority, which does not make
   * them faster - it makes them interruptible. React can stop half way through,
   * hand the thread back so a button press or a scroll is dealt with, and pick
   * the table up again. Without it the whole table is one indivisible task and
   * every tap during it is simply dropped.
   *
   * It is `rows` that is deferred and not the slice: a fresh array every render
   * never equals the last one, and a deferred value that always differs renders
   * everything twice forever.
   *
   * Measured over three runs each, drawing two hundred rows: the longest single
   * block of blocked thread was 125-144ms with this, and 136-254ms without. A
   * small effect, and an honest one - the cap above is what took this page from
   * 918ms of dead UI down to this range, and this only stops the bad run. It is
   * here and not on the other capped lists because this is the only one that can
   * really reach two hundred rows; a week of deliveries never does.
   */
  const settledRows = useDeferredValue(rows)

  /*
   * The table stops at two hundred; the totals underneath do not.
   *
   * Everything below this line is counted and added over `rows` - the whole
   * answer, every time. Only the drawing stops early, because drawing is the
   * expensive part and nothing else here is. A report whose figures changed
   * depending on how far the list happened to be drawn would be worth less than
   * no report at all.
   *
   * The server hands these back newest first, so the two hundred kept are the
   * two hundred anybody is looking for.
   */
  const shown = useMemo(
    () =>
      settledRows.length > LONG_LIST_CAP ? settledRows.slice(0, LONG_LIST_CAP) : settledRows,
    [settledRows],
  )

  /*
   * No total weight. Every row is a different flower - and کیش and خرچہ are
   * not flowers at all - so adding the column up gave a number with no meaning
   * that still looked like an answer. The per-row weights stay, because one row
   * is one flower.
   */
  const totals = useMemo(() => {
    let cash = 0
    let credit = 0
    for (const row of rows) {
      if (row.cash) cash += Number(row.amount)
      else credit += Number(row.amount)
    }
    return { cash, credit, all: cash + credit }
  }, [rows])

  return (
    <>
      <PageHead title="بکری کی رپورٹ" hint="کن دنوں میں کیا بکا — ایک ایک انٹری۔" />

      <Card>
        <div className="row">
          <Field label="اس تاریخ سے">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>

          <Field label="اس تاریخ تک">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>

        </div>

        {/* One tap for the spans actually asked for, so the date boxes are only
            touched when something unusual is wanted.

            There is no «سب». It fetched every sale ever written, which was
            already two thousand rows and grows every day, to answer a question
            nobody asks standing at a counter. Anything older than a month is
            still reachable by typing the two dates. */}
        <div className="btn-row">
          {(
            [
              ['آج', today(), today()],
              ['7 دن', daysBack(7), today()],
              ['30 دن', daysBack(30), today()],
            ] as const
          ).map(([label, start, end]) => (
            <button
              key={label}
              type="button"
              className={`btn btn--small ${from === start && to === end ? '' : 'btn--ghost'}`}
              onClick={() => {
                setFrom(start)
                setTo(end)
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {sales.error && <Note kind="error">{sales.error}</Note>}

      <div className="totals">
        <TotalBox label="نقد بکری" value={<Amt value={String(totals.cash)} />} />
        <TotalBox label="اُدھار بکری" value={<Amt value={String(totals.credit)} />} />
        <TotalBox label="کل بکری" value={<Amt value={String(totals.all)} />} accent />
        <TotalBox label="کل ادائیگی" value={<Amt value={taken.data?.total ?? '0'} />} />
      </div>

      <Card title={`انٹریاں (${rows.length})`}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>تاریخ اور وقت</th>
                <th>پھول</th>
                <th>نقد / گاہک</th>
                <th>وزن</th>
                <th>ریٹ</th>
                <th>رقم</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((sale) => (
                <tr key={sale.id} className={sale.cash ? undefined : 'row-payment'}>
                  <td>
                    <Dt value={sale.date} />
                    {/* The day repeats on every row of a day; the clock is the
                        only thing that tells two of them apart. Where it was
                        written on a different day from the one it is dated to,
                        that day is spelled out rather than implied. */}
                    <div className="muted small">
                      {formatEnteredAt(sale.enteredAt, sale.date)}
                    </div>
                  </td>
                  <td className="name-cell">{sale.itemName}</td>
                  <td className="name-cell">
                    {sale.cash ? <span className="muted">نقد</span> : sale.customerName}
                  </td>
                  <td className="num">
                    <Wt value={sale.weight} />
                  </td>
                  <td className="num">
                    <Amt value={sale.rate} />
                  </td>
                  <td className="num">
                    <strong>
                      <Amt value={sale.amount} />
                    </strong>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && !sales.loading && (
                <tr>
                  <td colSpan={6}>
                    <Empty>اِن دنوں میں کچھ نہیں بکا۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <LongListNote shown={shown.length} total={rows.length} />

        <p className="muted small" style={{ marginTop: 10 }}>
          اُدھار والی سطریں ہلکی سبز ہیں۔ انٹری ٹھیک کرنی ہو تو «نیا سودا» والے صفحے پر «پچھلے
          سودے» میں، یا گاہک کے «کھاتہ» میں۔
        </p>
      </Card>

      <DayAverageByItem />
    </>
  )
}
